import { useEffect } from 'react';
import { desktop } from '~/lib/desktop';

/**
 * Inside the desktop app: gives its title bar the chrome's colours (`--chrome`
 * behind, `--chrome-ink-2` for the text and the window buttons), so the title
 * bar, the top bar and the sidebar read as one surface, as in Slack. Sent once,
 * then again whenever the theme or its colours change or the system turns dark
 * or light. Mounted once, at the root; in a browser, or in a desktop app without
 * the member, it does nothing.
 */
export function useDesktopTitleBar(): void {
  useEffect(() => {
    const bridge = desktop();
    if (!bridge?.setTitleBarColors) return;
    const send = () => {
      const background = tokenHex('--chrome');
      const text = tokenHex('--chrome-ink-2');
      if (background && text) bridge.setTitleBarColors?.({ background, text });
    };
    send();
    const attrs = new MutationObserver(send);
    attrs.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme', 'data-palette'],
    });
    const scheme = window.matchMedia?.('(prefers-color-scheme: dark)');
    scheme?.addEventListener('change', send);
    return () => {
      attrs.disconnect();
      scheme?.removeEventListener('change', send);
    };
  }, []);
}

/** A colour token as the browser resolves it, as `#rrggbb` (the desktop app takes no other form). */
function tokenHex(token: string): string | null {
  const probe = document.createElement('span');
  probe.style.color = `var(${token})`;
  document.body.append(probe);
  const resolved = getComputedStyle(probe).color;
  probe.remove();
  return toHex(resolved);
}

/** `rgb(237, 239, 234)` or `rgba(…)` to `#edefea`; null for anything else. */
export function toHex(css: string): string | null {
  const m = /^rgba?\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)/.exec(css.trim());
  if (!m) return /^#[0-9a-f]{6}$/i.test(css.trim()) ? css.trim().toLowerCase() : null;
  return `#${m
    .slice(1, 4)
    .map((v) => Math.min(255, Number(v)).toString(16).padStart(2, '0'))
    .join('')}`;
}
